import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MediaItem } from '../models/media-item';
import { Game, GameStatuses } from '../models/game';
import { map } from 'rxjs/operators';
import { PaginatedResponse } from './paginated-response.interface';
import { ConfigService } from '../services/config.service';
import { applyGamesFilters, TournamentGamesFilters } from './tournament-api';

export interface GameMediaFilters extends TournamentGamesFilters {
  mediaCount?: number;
}

export interface MediaFilters {
  mediaType?: 'photo' | 'video' | 'live_video';
  sort?: string;
  teamId?: number;
  statuses?: GameStatuses[];
}

@Injectable({providedIn: 'root'})
export class MediaApi {
  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
  ) {
  }

  async getMedia(mediaId: number): Promise<MediaItem> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/media/${mediaId}/`).pipe(
      map(result => MediaItem.toFront(result))
    ).toPromise();
  }

  async getTournamentGameMedia(tournamentId: number, page: number, size: number, filters?: GameMediaFilters): Promise<PaginatedResponse<Game[]>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = applyGameMediaFilters(filters, params);

    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament/${tournamentId}/game_media/`, {observe: 'response', params}).pipe(
      map(response => {
        return {
          total: +response.headers.get('X-Page-Count'),
          data: Game.toFront(response.body)
        };
      })
    ).toPromise();
  }

  async getLeagueGameMedia(leagueId: number, page: number, size: number, filters?: GameMediaFilters): Promise<PaginatedResponse<Game[]>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = applyGameMediaFilters(filters, params);

    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/game_media/`, {observe: 'response', params}).pipe(
      map(response => {
        return {
          total: +response.headers.get('X-Page-Count'),
          data: Game.toFront(response.body)
        };
      })
    ).toPromise();
  }

  async getLeagueMedia(leagueId: number, page: number, size: number, filters: MediaFilters = {}): Promise<PaginatedResponse<MediaItem[]>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    if (filters.mediaType) {
      params = params.set('media_type', filters.mediaType);
    }
    if (filters.sort) {
      params = params.set('sort', filters.sort);
    }
    if (filters.teamId) {
      params = params.set('team_id', filters.teamId.toString());
    }
    if (filters.statuses) {
      const statuses = filters.statuses.map(i => GameStatuses[i]);
      params = params.set('statuses', statuses.join(','));
    }
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/media/`, {params, observe: 'response'})
      .pipe(map(response => ({
        total: +response.headers.get('X-Page-Count'),
        data: MediaItem.toFront(response.body)
      }))).toPromise();
  }

  async getGameMediaById(gameId: number): Promise<Game> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/game_media/${gameId}/`).pipe(
      map(result => Game.toFront(result))
    ).toPromise();
  }

  async getGameMedia(gameId: number): Promise<MediaItem[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/tournament_game/${gameId}/media/?size=1000`).pipe(
      map(result => MediaItem.toFront(result))
    ).toPromise();
  }

  async createZip(mediaIds: number[]): Promise<any> {
    return this.httpClient
      .post(`${this.configService.get('apiUrl')}/api/v1/media/zip/`, mediaIds, { responseType: 'blob' })
      .toPromise();
  }

  async downloadMedia(mediaId: number): Promise<any> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/media/${mediaId}/download/`,{responseType: 'blob'})
      .toPromise();
  }
}

export function applyGameMediaFilters(filters: GameMediaFilters, params: HttpParams, ): HttpParams {
  if (filters) {
    params = applyGamesFilters(filters, params);
    if (filters.tournamentTour) {
      params = params.set('tournament_tour', filters.tournamentTour.toString());
    }
    if (filters.teamId) {
      params = params.set('team_id', filters.teamId.toString());
    }
    if (filters.tournamentStageId) {
      params = params.set('tournament_stage_id', filters.tournamentStageId.toString());
    }
    if (filters.playoffId) {
      params = params.set('playoff_id', filters.playoffId.toString());
    }
    if (filters.playoffStage) {
      params = params.set('playoff_stage', filters.playoffStage.toString());
    }
    if (filters.playoffRound) {
      params = params.set('playoff_round', filters.playoffRound.toString());
    }
    if (filters.status) {
      params = params.set('status', GameStatuses[filters.status]);
    }
    if (filters.mediaCount) {
      params = params.set('media_count', filters.mediaCount.toString());
    }
  }
  return params;
}
